/**
 * 【法币展示/决策 统一逻辑】法币映射与 trade 页法币 tab 展示
 * 规则：1. 法币余额 2. 用户充值过的法币 3. IP 地区 → 命中则展示命中的；
 *       4. 若以上都不命中，则不展示法币 tab（返回空列表）；
 *       5. 自动选中优先级：余额 > 充值过 > IP。
 * 修改时请同步检查：ssr-web-index/utils/fiatMarket.js、web-index/utils/fiatMarket.js、
 * ssr-web-index @xt/Nav getCfdSymbol*.js、ssr-web-trade pages/cfd、market/tabs 等。
 */
import Storage from "utils/storage";
import { getFiatCodeByIp } from "./getIsIpInFiatCountries";

/** 法币市场支持的法币代码（仅三国：AED/BRL/SAR），与 ssr-web-index 一致 */
export const FIAT_MARKET_CODES = ["AED", "BRL", "SAR"];

/** tab 展示用的小写 key */
const FIAT_TAB_CODES = ["aed", "brl", "sar"];

/**
 * 从 Storage 的币种列表构建 coinId -> 法币代码 的映射（与 ssr-web-index getFiatCurrencyMapFromStorage 一致）
 * Storage key：currencies，结构 { version?, currencies: [{ id, currency }] }（与 index 的 currencyList 一致）
 */
function getFiatCurrencyMapFromStorage(): Record<string, string> {
  const data = Storage.get("currencies") || {};
  const currencies = (data as any)?.currencies || [];
  const map: Record<string, string> = {};
  currencies.forEach((item: any) => {
    const id = item.id ?? item.currencyId ?? item.coinId;
    const code = item.currency ?? item.currencyCode;
    if (id != null && code) {
      const upper = (code + "").toUpperCase();
      map[id] = upper;
      map[String(id)] = upper;
      map[Number(id)] = upper;
    }
  });
  return map;
}

/**
 * 根据 getUserInfo 返回的 fiatCurrencyIds，用 Storage 映射得到允许的法币代码（仅 AED/BRL/SAR）
 * 不请求接口，与 ssr-web-index resolveFiatCodesForList 中 fromFiatIds 逻辑一致
 */
export function getFiatCodesFromFiatCurrencyIds(fiatCurrencyIds?: number[] | null): string[] {
  if (!fiatCurrencyIds || !Array.isArray(fiatCurrencyIds) || fiatCurrencyIds.length === 0) {
    return [];
  }
  const map = getFiatCurrencyMapFromStorage();
  const codes: string[] = [];
  fiatCurrencyIds.forEach((rawId) => {
    const code = map[rawId] ?? map[String(rawId)] ?? map[Number(rawId)];
    if (code && !codes.includes(code)) {
      codes.push(code);
    }
  });
  return codes.filter((c) => FIAT_MARKET_CODES.includes(c));
}

/**
 * 合并允许展示的法币：有余额 + fiatCurrencyIds 映射 + IP；都不命中时返回空列表（不展示法币 tab）
 * 自动选中优先级：余额 > 充值过 > IP
 */
function mergeAllowedFiatCodes(
  fromFiatIds: string[],
  fromIP: string | null,
  fiatCodesWithBalance: string[] = []
): string[] {
  const fromIPLower = fromIP ? fromIP.toLowerCase() : null;
  const fromIdsLower = fromFiatIds.map((c) => c.toLowerCase());
  const balanceLower = fiatCodesWithBalance.map((c) => (c || "").toLowerCase()).filter((c) => FIAT_TAB_CODES.includes(c));
  const order: string[] = [];
  balanceLower.forEach((c) => {
    if (!order.includes(c)) order.push(c);
  });
  fromIdsLower.forEach((c) => {
    if (FIAT_TAB_CODES.includes(c) && !order.includes(c)) order.push(c);
  });
  if (fromIPLower && FIAT_TAB_CODES.includes(fromIPLower) && !order.includes(fromIPLower)) {
    order.push(fromIPLower);
  }
  return order;
}

/**
 * 根据 fiatCurrencyIds + 法币余额 + IP 获取允许展示的法币 tab 代码列表（与 ssr-web-index 市场逻辑一致）
 * - fiatCurrencyIds 来自 getUserInfo，用 Storage 币种列表做映射
 * - fiatCodesWithBalance 为有法币余额的代码列表（需调用方拉取 getFiatListByBalance 后传入）
 * - 无命中时展示全部法币 tab（兜底）
 */
export async function getAllowedFiatCodesForTabs(
  fiatCurrencyIds?: number[] | null,
  fiatCodesWithBalance?: string[]
): Promise<string[]> {
  const fromIds = getFiatCodesFromFiatCurrencyIds(fiatCurrencyIds);
  const fromIP = await getFiatCodeByIp();
  return mergeAllowedFiatCodes(fromIds, fromIP, fiatCodesWithBalance || []);
}
